// TEMPORARY DIRECT QUERY VERSION - Replace TransactionHistory.js with this for testing

import React, { useState, useEffect } from 'react';
import { 
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { collection, query, where, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import TransactionStatus from '../status/TransactionStatus';
import downloadManager from '../../utils/downloadManager';

const TransactionHistory = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // Direct Firestore query - bypassing useTransactions hook
  useEffect(() => {
    if (!currentUser) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    console.log('DIRECT QUERY - Setting up for user:', currentUser.uid);

    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        console.log('DIRECT QUERY - Snapshot received, docs:', snapshot.size);
        
        const transactionList = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('DIRECT QUERY - Transaction:', doc.id, data.status, data.fileName);
          
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date()
          };
        });
        
        // Sort by newest first
        transactionList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        setTransactions(transactionList);
        setLoading(false);
        setError(null);
        
        console.log('DIRECT QUERY - Transactions updated:', transactionList.length);
      },
      (err) => {
        console.error('DIRECT QUERY - Error:', err);
        setError('Failed to load transaction history');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Handle CSV download using DownloadManager for user-friendly filenames
  const handleDownload = async (transaction) => {
    console.log('Download requested for:', transaction.id, transaction.downloadUrl);
    
    try {
      // Set downloading state
      setDownloadingIds(prev => new Set(prev).add(transaction.id));
      
      // Use DownloadManager for proper filename handling
      await downloadManager.downloadCSV(
        transaction,
        (progress) => {
          // Optional: Handle progress updates
          console.log(`Download progress: ${progress}%`);
        },
        (transactionId, status, extra) => {
          // Optional: Handle status updates
          console.log(`Transaction ${transactionId} status: ${status}`);
        }
      );
      
      console.log('✅ Download completed via DownloadManager');
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      alert('Download failed: ' + error.message);
    } finally {
      // Clear downloading state
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(transaction.id);
        return newSet;
      });
    }
  };


  // Handle transaction retry
  const handleRetry = async (transaction) => {
    alert('Retry functionality - please upload the file again.');
  };

  // Handle transaction delete
  const handleDelete = (transaction) => {
    setDeleteConfirmation(transaction);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    try {
      // Set deleting state
      setDeletingIds(prev => new Set(prev).add(deleteConfirmation.id));
      
      // Delete from Firestore
      const transactionRef = doc(db, 'transactions', deleteConfirmation.id);
      await deleteDoc(transactionRef);
      
      console.log('✅ Transaction deleted successfully:', deleteConfirmation.id);
      
      // Clear confirmation
      setDeleteConfirmation(null);
      
    } catch (error) {
      console.error('❌ Failed to delete transaction:', error);
      alert('Failed to delete conversion: ' + error.message);
    } finally {
      // Clear deleting state
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(deleteConfirmation?.id);
        return newSet;
      });
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h2>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  console.log('RENDERING - Transactions to display:', transactions.length);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
          <div className="flex items-center text-sm text-gray-500">
            <ClockIcon className="h-4 w-4 mr-1" />
            {transactions.length} transactions
          </div>
        </div>
      </div>

      <div className="p-6">
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
            <p className="text-gray-500 mb-4">
              Upload your first bank statement to get started
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="relative">
                <TransactionStatus
                  transaction={transaction}
                  onDownload={handleDownload}
                  onRetry={handleRetry}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Conversion
              </h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete the conversion for "{deleteConfirmation.fileName}"? 
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex space-x-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200"
                disabled={deletingIds.has(deleteConfirmation.id)}
              >
                {deletingIds.has(deleteConfirmation.id) ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;